import { useState, useCallback } from 'react';
import {
  ChevronRight,
  Plus,
  X,
  GripVertical,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { PREDEFINED_FIELD_GROUPS } from '@siliconvault/shared';
import type { CustomFieldDef } from '@siliconvault/shared';
import { Input, Select } from '../ui/Input';
import { Button } from '../ui/Button';
import { useTranslation } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import styles from '../../routes/vaults.custom.module.css';

const FIELD_TYPE_KEYS: { value: CustomFieldDef['type']; tKey: TranslationKey }[] = [
  { value: 'text', tKey: 'fieldEditor.type.text' },
  { value: 'number', tKey: 'fieldEditor.type.number' },
  { value: 'select', tKey: 'fieldEditor.type.select' },
  { value: 'date', tKey: 'fieldEditor.type.date' },
  { value: 'boolean', tKey: 'fieldEditor.type.boolean' },
  { value: 'url', tKey: 'fieldEditor.type.url' },
];

interface VaultFieldEditorProps {
  fields: CustomFieldDef[];
  onFieldsChange: (fields: CustomFieldDef[]) => void;
  platformLabel: string;
}

export function VaultFieldEditor({ fields, onFieldsChange, platformLabel }: VaultFieldEditorProps) {
  const { t } = useTranslation();
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<CustomFieldDef['type']>('text');
  const [newFieldPlaceholder, setNewFieldPlaceholder] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState('');
  const [builtinExpanded, setBuiltinExpanded] = useState(true);

  const fieldKeys = new Set(fields.map((f) => f.key));

  const getDuplicateWarning = useCallback((label: string, key: string): string | null => {
    const lower = (label + ' ' + key).toLowerCase();
    const pl = platformLabel || 'Brand';
    if (/\b(manufacturer|brand|maker|producer)\b/.test(lower))
      return t('fieldEditor.warn.platform').replace(/\{label\}/g, pl);
    if (/\b(description|desc)\b/.test(lower))
      return t('fieldEditor.warn.description');
    if (/\b(notes|custom.?notes)\b/.test(lower))
      return t('fieldEditor.warn.notes');
    if (/\b(title)\b/.test(lower) && !/\bsub/.test(lower))
      return t('fieldEditor.warn.name');
    if (/\b(category|categories)\b/.test(lower) && !/\bsub/.test(lower))
      return t('fieldEditor.warn.category');
    if (/\b(condition|state)\b/.test(lower))
      return t('fieldEditor.warn.condition');
    if (/\b(price|cost)\b/.test(lower))
      return t('fieldEditor.warn.price');
    if (/\b(location|place|storage)\b/.test(lower))
      return t('fieldEditor.warn.location');
    return null;
  }, [platformLabel, t]);

  const addPredefinedField = useCallback((field: CustomFieldDef) => {
    onFieldsChange(
      fields.some((f) => f.key === field.key)
        ? fields.filter((f) => f.key !== field.key)
        : fields.length >= 20 ? fields : [...fields, field],
    );
  }, [fields, onFieldsChange]);

  const removeField = useCallback((key: string) => {
    onFieldsChange(fields.filter((f) => f.key !== key));
  }, [fields, onFieldsChange]);

  const generateKey = (label: string) =>
    label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 50) || '';

  const handleAddCustomField = () => {
    const key = newFieldKey || generateKey(newFieldLabel);
    if (!key || !newFieldLabel || fieldKeys.has(key)) return;

    const field: CustomFieldDef = {
      key,
      label: newFieldLabel,
      type: newFieldType,
      ...(newFieldPlaceholder && { placeholder: newFieldPlaceholder }),
      ...(newFieldType === 'select' && newFieldOptions.length >= 2 && { options: [...newFieldOptions] }),
    };

    onFieldsChange([...fields, field]);
    resetAddFieldForm();
  };

  const resetAddFieldForm = () => {
    setNewFieldKey('');
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldPlaceholder('');
    setNewFieldOptions([]);
    setNewOptionInput('');
    setShowAddField(false);
  };

  const addOption = () => {
    const trimmed = newOptionInput.trim();
    if (!trimmed || newFieldOptions.includes(trimmed)) return;
    setNewFieldOptions((prev) => [...prev, trimmed]);
    setNewOptionInput('');
  };

  const removeOption = (opt: string) => {
    setNewFieldOptions((prev) => prev.filter((o) => o !== opt));
  };

  return (
    <>
      {/* Built-in fields info */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Info size={15} style={{ verticalAlign: '-2px', marginRight: 6, opacity: 0.6 }} />
          {t('fieldEditor.includedByDefault')}
        </h2>
        <div className={styles.builtinBlock}>
          <button
            type="button"
            className={styles.builtinToggle}
            onClick={() => setBuiltinExpanded(!builtinExpanded)}
          >
            <ChevronRight
              size={14}
              className={`${styles.builtinChevron}${builtinExpanded ? ` ${styles.builtinChevronOpen}` : ''}`}
            />
            {t('fieldEditor.builtinToggle')}
          </button>
          {builtinExpanded && (
            <div className={styles.builtinContent}>
              <div className={styles.builtinGroupLabel}>{t('fieldEditor.groupCore')}</div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldName')}</span>
                <span className={styles.builtinBadge}>{t('fieldEditor.required')}</span>
              </div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldCategory')}</span>
                <span className={styles.builtinBadge}>{t('fieldEditor.required')}</span>
              </div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{platformLabel || 'Brand'}</span>
                <span className={styles.builtinBadge}>{t('fieldEditor.required')}</span>
              </div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldSubcategory')}</span>
              </div>

              <div className={styles.builtinGroupLabel}>{t('fieldEditor.groupStatus')}</div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldCondition')}</span>
                <span className={styles.builtinBadge}>{t('fieldEditor.required')}</span>
              </div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldLocation')}</span>
              </div>

              <div className={styles.builtinGroupLabel}>{t('fieldEditor.groupFinancial')}</div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldAcquisition')}</span>
              </div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldLaunch')}</span>
              </div>

              <div className={styles.builtinGroupLabel}>{t('fieldEditor.groupNotes')}</div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldDescription')}</span>
              </div>
              <div className={styles.builtinField}>
                <span className={styles.builtinFieldName}>{t('fieldEditor.fieldCustomNotes')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Predefined Fields */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('fieldEditor.additionalFields')}</h2>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '0 0 16px' }}>
          {t('fieldEditor.additionalFieldsDesc')}
        </p>

        {PREDEFINED_FIELD_GROUPS.map((group) => (
          <div key={group.label} className={styles.predefinedGroup}>
            <div className={styles.predefinedGroupLabel}>{t(`fieldEditor.group.${group.label}` as TranslationKey)}</div>
            <div className={styles.predefinedList}>
              {group.fields.map((field) => {
                const added = fieldKeys.has(field.key);
                return (
                  <button
                    key={field.key}
                    type="button"
                    className={`${styles.predefinedChip}${added ? ` ${styles.predefinedChipAdded}` : ''}`}
                    onClick={() => addPredefinedField(field)}
                    disabled={!added && fields.length >= 20}
                  >
                    {added ? <X size={12} /> : <Plus size={12} />}
                    {t(`fieldEditor.predef.${field.key}` as TranslationKey)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Selected fields */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {t('fieldEditor.selectedFields').replace('{count}', String(fields.length))}
        </h2>

        {fields.length === 0 ? (
          <div className={styles.emptyFields}>
            {t('fieldEditor.emptyFields')}
          </div>
        ) : (
          <div className={styles.fieldsList}>
            {fields.map((field) => {
              const warning = getDuplicateWarning(field.label, field.key);
              return (
                <div key={field.key} className={styles.fieldCard}>
                  <div className={styles.fieldCardDragHandle}>
                    <GripVertical size={16} />
                  </div>
                  <div className={styles.fieldCardInfo}>
                    <p className={styles.fieldCardLabel}>{(() => { const k = `fieldEditor.predef.${field.key}` as TranslationKey; const v = t(k); return v !== k ? v : field.label; })()}</p>
                    <p className={styles.fieldCardMeta}>
                      {field.type}
                      {field.options ? ` (${field.options.join(', ')})` : ''}
                      {field.placeholder ? ` — ${field.placeholder}` : ''}
                    </p>
                    {warning && (
                      <div className={styles.fieldWarning}>
                        <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                        {warning}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.fieldCardRemove}
                    onClick={() => removeField(field.key)}
                    title={t('fieldEditor.removeField')}
                  >
                    <X size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add custom field */}
        {!showAddField ? (
          <button
            type="button"
            className={styles.addFieldToggle}
            onClick={() => setShowAddField(true)}
            disabled={fields.length >= 20}
          >
            <Plus size={14} />
            {t('fieldEditor.addCustomField')}
          </button>
        ) : (
          <div className={styles.addFieldForm}>
            <Input
              label={t('fieldEditor.fieldLabel')}
              value={newFieldLabel}
              onChange={(e) => {
                setNewFieldLabel(e.target.value);
                if (!newFieldKey || newFieldKey === generateKey(newFieldLabel)) {
                  setNewFieldKey(generateKey(e.target.value));
                }
              }}
              placeholder={t('fieldEditor.fieldLabelPlaceholder')}
            />
            <div>
              <Select
                label={t('fieldEditor.fieldType')}
                value={newFieldType}
                onChange={(e) => setNewFieldType(e.target.value as CustomFieldDef['type'])}
              >
                {FIELD_TYPE_KEYS.map((ft) => (
                  <option key={ft.value} value={ft.value}>{t(ft.tKey)}</option>
                ))}
              </Select>
            </div>
            <div className={styles.addFieldFormFull}>
              <Input
                label={t('fieldEditor.fieldKey')}
                value={newFieldKey}
                onChange={(e) => setNewFieldKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder={t('fieldEditor.fieldKeyPlaceholder')}
              />
            </div>
            {newFieldType !== 'boolean' && newFieldType !== 'select' && (
              <div className={styles.addFieldFormFull}>
                <Input
                  label={t('fieldEditor.placeholderLabel')}
                  value={newFieldPlaceholder}
                  onChange={(e) => setNewFieldPlaceholder(e.target.value)}
                  placeholder={t('fieldEditor.placeholderPlaceholder')}
                />
              </div>
            )}
            {newFieldType === 'select' && (
              <div className={styles.addFieldFormFull}>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  {t('fieldEditor.optionsLabel')}
                </label>
                <div className={styles.optionsRow}>
                  {newFieldOptions.map((opt) => (
                    <span key={opt} className={styles.optionTag}>
                      {opt}
                      <button type="button" className={styles.optionTagRemove} onClick={() => removeOption(opt)}>
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  <input
                    className={styles.optionsInput}
                    value={newOptionInput}
                    onChange={(e) => setNewOptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addOption(); }
                    }}
                    placeholder={t('fieldEditor.optionsPlaceholder')}
                  />
                </div>
              </div>
            )}
            <div className={styles.addFieldActions}>
              <Button variant="ghost" size="small" onClick={resetAddFieldForm}>
                {t('vaults.cancel')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleAddCustomField}
                disabled={
                  !newFieldLabel.trim() ||
                  !newFieldKey.trim() ||
                  fieldKeys.has(newFieldKey) ||
                  (newFieldType === 'select' && newFieldOptions.length < 2)
                }
              >
                {t('fieldEditor.addField')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
